// Regras do envio automático da ENTRADA do acordo de parcelamento e dos avisos
// que o card do acordo mostra quando o boleto ou o termo não chegou.
//
// Pedido do Vinícius em 24/09/2026: a entrada sai sozinha, junto com o termo —
// emitida e mandada por WhatsApp e e-mail para quem paga —, e se algo falhar a
// tela tem que dizer. Antes a fatura da entrada nascia sem boleto e ficava parada:
// o Hortifruti assinou e ficou sem ter o que pagar, sem ninguém perceber.
//
// Mudou em 16/10/2026 (pedido do Vinícius): o boleto continua sendo emitido quando
// o termo sai, mas só vai para o cliente DEPOIS que ele assina. Quem manda é a
// varredura (efetivar-parcelamentos), que é quem vê a assinatura.
//
// Sem I/O: roda sob `node --test` (src/lib/__tests__/entradaAcordoRegras.test.ts).
// Quem executa o envio é src/lib/entradaAcordo.ts.

import {
  aguardaAssinatura,
  dataEmBrasilia,
  entradaVencidaParaEnviar,
  motivoEntradaVencida,
  type EntradaEnvio,
} from '../../supabase/functions/_shared/envioEntradaAcordo.ts';

// O que o servidor também usa mora em _shared (a varredura manda o boleto quando
// o termo é assinado). Reexportado aqui para a tela não mudar de import.
export {
  aguardaAssinatura,
  canaisPendentes,
  contatoDaCobranca,
  dataEmBrasilia,
  descreverEnvio,
  entradaVencidaParaEnviar,
  erroVencimentoEntrada,
  guardarAteAssinar,
  motivoEntradaVencida,
  motivoSemContato,
  PRAZO_MINIMO_ENTRADA_DIAS,
  telefoneWhatsapp,
  type CanalEnvio,
  type ContatoCobranca,
  type EntradaEnvio,
  type EtapaEnvio,
} from '../../supabase/functions/_shared/envioEntradaAcordo.ts';

export interface EntregaParte {
  email: string | null;
  entregue_em: string | null;
  recusado_em: string | null;
  motivo: string | null;
  /** Canal do convite do Autentique (#6203). Ausente nos registros antigos. */
  canal?: 'whatsapp' | 'email' | 'outro' | null;
  /** Quando a parte abriu o termo. */
  visualizado_em?: string | null;
}

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', {
  timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});

/**
 * Uma linha sobre o convite do termo para o card do acordo (#6203):
 * "Enviado pelo WhatsApp · aberto em 08/10, 14:02". Vazio sem registro da varredura.
 */
export function descreverEntregaTermo(parte: EntregaParte | null | undefined): string {
  if (!parte) return '';
  const canal = parte.canal === 'whatsapp' ? 'Enviado pelo WhatsApp'
    : parte.canal === 'email' ? `Enviado por e-mail${parte.email ? ` (${parte.email})` : ''}`
    : 'Termo enviado';
  if (parte.recusado_em) return `${canal} · ${parte.canal === 'whatsapp' ? 'não chegou' : 'o e-mail voltou, não chegou'}`;
  if (parte.visualizado_em) return `${canal} · aberto em ${dataHora(parte.visualizado_em)}`;
  if (parte.entregue_em) return `${canal} · entregue, ainda não aberto`;
  return `${canal} · ainda não aberto`;
}

/** `parcelamentos.termo_entrega`, gravado pela varredura (efetivar-parcelamentos). */
export interface TermoEntrega {
  conferido_em: string;
  devedor?: EntregaParte | null;
  coobrigado?: EntregaParte | null;
}

const texto = (v: unknown) => String(v ?? '').trim();

export type NivelAviso = 'erro' | 'atencao';
export type AcaoAviso = 'reenviar_entrada' | 'enviar_termo';

export interface AvisoAcordo {
  nivel: NivelAviso;
  titulo: string;
  detalhe: string;
  acao?: AcaoAviso;
}

export interface AcordoParaAvisos {
  status: string;
  termo_id_autentique?: string | null;
  termo_enviado_em?: string | null;
  termo_envio_erro?: string | null;
  termo_entrega?: TermoEntrega | null;
  entrada_envio?: EntradaEnvio | null;
  /** A fatura da entrada, como está hoje. */
  entrada?: { num_cobranca_boleto?: string | null; status_boleto_vigor?: string | null; vencimento_boleto?: string | null } | null;
}

const ESTADOS_COM_AVISO = new Set(['aguardando_assinatura', 'aguardando_entrada']);

function avisoEntregaTermo(parte: EntregaParte | null | undefined, quem: string): AvisoAcordo | null {
  if (!parte?.recusado_em) return null;
  return {
    nivel: 'erro',
    titulo: `O termo não chegou ${quem}`,
    detalhe: `O e-mail ${parte.email || ''} devolveu a mensagem${parte.motivo ? ` (${parte.motivo})` : ''}. `
      + 'Confirme o e-mail com o cliente, cancele o acordo e crie de novo com o endereço certo.',
  };
}

/**
 * O que o card do acordo precisa mostrar. Lista vazia = nada a avisar.
 *
 * Só vale enquanto o acordo espera assinatura ou entrada: depois de efetivado,
 * cancelado ou expirado, o envio da entrada não é mais a pendência.
 */
export function avisosDoAcordo(a: AcordoParaAvisos, hoje: string = dataEmBrasilia()): AvisoAcordo[] {
  if (!ESTADOS_COM_AVISO.has(a.status)) return [];
  const avisos: AvisoAcordo[] = [];

  // --- Termo -------------------------------------------------------------
  if (!a.termo_id_autentique && a.termo_envio_erro) {
    avisos.push({
      nivel: 'erro',
      titulo: 'O termo NÃO foi enviado',
      detalhe: a.termo_envio_erro,
      acao: 'enviar_termo',
    });
  }
  const devedor = avisoEntregaTermo(a.termo_entrega?.devedor, 'ao titular');
  if (devedor) avisos.push(devedor);
  const coobrigado = avisoEntregaTermo(a.termo_entrega?.coobrigado, 'ao responsável pela dívida');
  if (coobrigado) avisos.push(coobrigado);

  // --- Boleto da entrada ---------------------------------------------------
  // Só depois do termo: é junto com ele que a entrada é emitida.
  if (!a.termo_id_autentique) return avisos;
  const statusBoleto = texto(a.entrada?.status_boleto_vigor).toLowerCase();
  if (statusBoleto === 'pago' || statusBoleto.startsWith('cancelad')) return avisos;

  const envio = a.entrada_envio;
  if (envio?.pulado) {
    avisos.push({ nivel: 'atencao', titulo: 'O boleto da entrada não sai sozinho', detalhe: envio.pulado });
    return avisos;
  }

  const assinado = a.status === 'aguardando_entrada';
  if (envio?.emissao && !envio.emissao.ok) {
    avisos.push({
      nivel: 'erro',
      titulo: 'O boleto da entrada NÃO foi emitido',
      detalhe: envio.emissao.erro || 'A emissão falhou sem dizer o motivo.',
      acao: 'reenviar_entrada',
    });
    return avisos;
  }

  // A entrada venceu antes da assinatura e o boleto não saiu. Depois que o
  // analista emite com data nova, o card oferece mandar o boleto novo.
  if (envio?.vencida) {
    const reemitida = texto(a.entrada?.vencimento_boleto).slice(0, 10) > envio.vencida;
    avisos.push(reemitida
      ? {
        nivel: 'atencao',
        titulo: 'Boleto novo da entrada pronto para mandar',
        detalhe: 'A entrada já tem vencimento novo. Mande para o cliente.',
        acao: 'reenviar_entrada',
      }
      : { nivel: 'erro', titulo: 'A entrada venceu antes da assinatura', detalhe: motivoEntradaVencida(envio.vencida) });
    return avisos;
  }

  if (!envio && !texto(a.entrada?.num_cobranca_boleto)) {
    avisos.push({
      nivel: 'atencao',
      titulo: 'O boleto da entrada ainda não foi emitido',
      detalhe: assinado
        ? 'O cliente assinou, mas ainda não tem o boleto da entrada para pagar.'
        : 'Emita agora. Ele vai para o cliente quando o termo for assinado.',
      acao: 'reenviar_entrada',
    });
    return avisos;
  }

  // Boleto guardado até a assinatura: antes dela não há o que avisar. Depois,
  // a varredura manda em até 15 min; se ainda estiver guardado, ela não passou.
  if (aguardaAssinatura(envio)) {
    if (assinado) {
      avisos.push({
        nivel: 'atencao',
        titulo: 'O boleto da entrada ainda não foi para o cliente',
        detalhe: 'O cliente assinou. O boleto sai sozinho em até 15 minutos — ou envie agora.',
        acao: 'reenviar_entrada',
      });
    } else if (entradaVencidaParaEnviar(a.entrada?.vencimento_boleto, hoje)) {
      // #6553: o acordo espera até 30 dias pela assinatura, mas a entrada vence
      // antes. Sem este aviso ninguém via até o cliente assinar (o boleto não
      // sai) ou reclamar que não tem o que pagar.
      const venc = texto(a.entrada?.vencimento_boleto).slice(0, 10).split('-').reverse().join('/');
      avisos.push({
        nivel: 'erro',
        titulo: 'A entrada venceu e o termo ainda não foi assinado',
        detalhe: `O boleto da entrada venceu em ${venc}. Se o cliente assinar agora, esse boleto não vai para ele. `
          + 'Cancele o boleto e emita a entrada com data nova pela tela de Faturas. Depois, lembre o cliente de assinar o termo.',
      });
    }
    return avisos;
  }

  // Assinado, boleto emitido e nenhum canal tentado: o envio parou no meio.
  if (assinado && envio?.emissao?.ok && !envio.whatsapp && !envio.email) {
    avisos.push({
      nivel: 'atencao',
      titulo: 'O boleto da entrada ainda não foi para o cliente',
      detalhe: 'O boleto está emitido, mas não saiu por WhatsApp nem por e-mail.',
      acao: 'reenviar_entrada',
    });
    return avisos;
  }

  const falhas = (['whatsapp', 'email'] as const)
    .map((canal) => ({ canal, etapa: envio?.[canal] }))
    .filter((c) => c.etapa && !c.etapa.ok);
  if (falhas.length === 0) return avisos;

  const nomeCanal = { whatsapp: 'WhatsApp', email: 'e-mail' } as const;
  const detalhe = falhas
    .map((f) => `${nomeCanal[f.canal]}${f.etapa?.destino ? ` (${f.etapa.destino})` : ''}: ${f.etapa?.erro || 'falhou'}`)
    .join(' · ');
  const chegouPorAlgum = (['whatsapp', 'email'] as const).some((c) => envio?.[c]?.ok);
  avisos.push({
    nivel: chegouPorAlgum ? 'atencao' : 'erro',
    titulo: chegouPorAlgum
      ? `O boleto da entrada não chegou pelo ${falhas.map((f) => nomeCanal[f.canal]).join(' nem pelo ')}`
      : 'O boleto da entrada NÃO chegou ao cliente',
    detalhe,
    acao: 'reenviar_entrada',
  });
  return avisos;
}

const semPonto = (v: unknown) => String(v ?? '').trim().replace(/[.\s]+$/, '');

/** Aviso curto para o analista logo depois de enviar o termo (toast). */
export function mensagemEnvioEntrada(e: EntradaEnvio | null | undefined): { tipo: 'success' | 'warning'; texto: string } | null {
  if (!e) return null;
  if (e.pulado) return { tipo: 'warning', texto: `Boleto da entrada: ${semPonto(e.pulado)}.` };
  if (e.emissao && !e.emissao.ok) {
    return { tipo: 'warning', texto: `O boleto da entrada NÃO foi emitido: ${semPonto(e.emissao.erro)}. Veja no card do acordo.` };
  }
  if (e.vencida) return { tipo: 'warning', texto: motivoEntradaVencida(e.vencida) };
  if (aguardaAssinatura(e)) {
    return { tipo: 'success', texto: 'Boleto da entrada emitido. Ele vai para o cliente por WhatsApp e e-mail quando o termo for assinado.' };
  }
  const falhas = [
    e.whatsapp && !e.whatsapp.ok ? `WhatsApp (${semPonto(e.whatsapp.erro)})` : null,
    e.email && !e.email.ok ? `e-mail (${semPonto(e.email.erro)})` : null,
  ].filter(Boolean);
  if (falhas.length) {
    return { tipo: 'warning', texto: `Boleto da entrada emitido, mas não chegou por: ${falhas.join(' e ')}. Veja no card do acordo.` };
  }
  return { tipo: 'success', texto: 'Boleto da entrada emitido e enviado por WhatsApp e e-mail.' };
}
